/* ══════════════════════════════════════════════════════════════
   plausible.ts — Privacy-Respecting Analytics
   Plausible Analytics — no cookies, GDPR-compliant
   ══════════════════════════════════════════════════════════════ */

interface PlausibleConfig {
    domain: string;
    apiHost: string;
    enabled: boolean;
}

interface PlausibleEventData {
    name: string;
    props?: Record<string, string | number | boolean>;
    url?: string;
    referrer?: string;
}

const STORAGE_KEY = 'imm-analytics-optout';

function getConfig(): PlausibleConfig {
    return {
        domain: 'indiamonitor.app',
        apiHost: 'https://plausible.io',
        enabled: !isOptedOut(),
    };
}

/**
 * Check if user has opted out of analytics
 */
export function isOptedOut(): boolean {
    return localStorage.getItem(STORAGE_KEY) === 'true';
}

/**
 * Set analytics opt-out
 */
export function setOptOut(optOut: boolean): void {
    localStorage.setItem(STORAGE_KEY, optOut ? 'true' : 'false');
}

/**
 * Initialize Plausible analytics script
 */
export function initAnalytics(): void {
    const config = getConfig();
    if (!config.enabled) {
        console.info('[Analytics] Opted out — no tracking');
        return;
    }

    // Don't load in development
    if (import.meta.env.DEV) {
        console.info('[Analytics] Skipped in development mode');
        return;
    }

    // Load Plausible script
    const script = document.createElement('script');
    script.defer = true;
    script.setAttribute('data-domain', config.domain);
    script.setAttribute('data-api', `${config.apiHost}/api/event`);
    script.src = `${config.apiHost}/js/script.js`;
    document.head.appendChild(script);

    console.info('[Analytics] Plausible loaded for', config.domain);
}

/**
 * Track a custom event
 */
export function trackEvent(eventData: PlausibleEventData): void {
    const config = getConfig();
    if (!config.enabled) return;

    // Use Plausible's global function if available
    const plausible = (window as unknown as Record<string, unknown>).plausible as
        ((event: string, options?: { props?: Record<string, string | number | boolean> }) => void) | undefined;

    if (plausible) {
        plausible(eventData.name, { props: eventData.props });
        return;
    }

    // Fallback: direct API call
    if (import.meta.env.DEV) return;

    const payload = {
        n: eventData.name,
        u: eventData.url || window.location.href,
        d: config.domain,
        r: eventData.referrer || document.referrer || null,
        p: eventData.props ? JSON.stringify(eventData.props) : undefined,
    };

    fetch(`${config.apiHost}/api/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
    }).catch(() => { /* Analytics should never break the app */ });
}

// ─── Pre-built Event Trackers ───

/** Track page view */
export function trackPageView(page: string): void {
    trackEvent({ name: 'pageview', props: { page } });
}

/** Track strategy view */
export function trackStrategyView(strategy: string, confidence: number): void {
    trackEvent({
        name: 'Strategy View',
        props: { strategy, confidence: Math.round(confidence * 100) },
    });
}

/** Track language switch */
export function trackLanguageSwitch(from: string, to: string): void {
    trackEvent({
        name: 'Language Switch',
        props: { from, to },
    });
}

/** Track broker connection */
export function trackBrokerConnect(broker: string): void {
    trackEvent({ name: 'Broker Connect', props: { broker } });
}

/** Track tool usage */
export function trackToolUsage(tool: string): void {
    trackEvent({ name: 'Tool Used', props: { tool } });
}

/** Track chat message send */
export function trackChatMessageSent(surface: 'page' | 'widget', language: string): void {
    trackEvent({
        name: 'Chat Message Sent',
        props: { surface, language },
    });
}

/** Track chat response receive */
export function trackChatMessageReceived(surface: 'page' | 'widget', intent: string): void {
    trackEvent({
        name: 'Chat Message Received',
        props: { surface, intent },
    });
}

/** Track chat feedback */
export function trackChatFeedback(rating: 'up' | 'down'): void {
    trackEvent({
        name: 'Chat Feedback',
        props: { rating },
    });
}
